import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { SET_USER_CIRCLE, UPDATE_SHIPPING_PARTNER } from '../../mutations';
import { ORDER_COMPLETED_EMAIL_FLAG } from '../../constants';

const saveError = { severity: 'error', message: 'Could not save that setting. Please try again.' };

/**
 * The Details page's manifest sharing and order-completed email saves. The carrier can change the
 * email only until the partnership is connected; after that the shipper owns it.
 * @param {Object} partnership the selected partnership, with `shipper.user.circles`
 * @param {Function} notify shows a snackbar message
 */
export const useShipperDetails = (partnership, notify) => {
    const onError = (e) => {
        console.error(e);
        captureException(e);
        notify(saveError);
    };
    const [updateShipper, { loading: savingManifests }] = useMutation(UPDATE_SHIPPING_PARTNER, { onError });
    const [setUserCircle, { loading: savingEmail }] = useMutation(SET_USER_CIRCLE, { onError });

    const circles = partnership.shipper?.user?.circles || {};

    const setShareManifests = useCallback(
        (share_manifests) =>
            updateShipper({
                variables: { partnership_id: partnership.partnership_id, update: { share_manifests } },
            }),
        [updateShipper, partnership.partnership_id]
    );

    // Merged server-side, so the shipper's other flags are never overwritten from a stale copy.
    const setOrderCompletedEmail = useCallback(
        (enabled) =>
            setUserCircle({
                variables: { user_id: partnership.shipper.client_id, patch: { [ORDER_COMPLETED_EMAIL_FLAG]: enabled } },
            }),
        [setUserCircle, partnership.shipper?.client_id]
    );

    return {
        shareManifests: !!partnership.share_manifests,
        setShareManifests,
        savingManifests,
        orderCompletedEmail: !!circles[ORDER_COMPLETED_EMAIL_FLAG],
        setOrderCompletedEmail,
        savingEmail,
        shipperOwnsEmail: partnership.status === 'APPROVED',
    };
};
